/**
 * Primitivas de interfaz — `OD-0017 D-01` (sistema propio y mínimo) y `D-04`.
 *
 * Aquí sólo hay lo que hoy se usa. Diálogos, menús y combos —donde la
 * accesibilidad es difícil de verdad— NO se improvisan: cuando hagan falta,
 * `D-01` manda usar primitivas headless accesibles.
 *
 * Ningún texto visible se escribe aquí: llega por props desde `i18n/es.ts`.
 */
import { useState, type ReactNode } from "react";
import { opcionQueCoincide } from "./coincide";
import { Icono } from "./Iconos";

/**
 * Las cuatro variantes de `OD-0017 D-01`, y no más:
 *
 *   principal   la acción del bloque, una sola por bloque
 *   secundario  el resto de acciones con borde
 *   terciario   se lee como un enlace, pero hace algo aquí
 *   peligro     irreversible (anular, descartar). El color NO va solo: el
 *               texto del botón dice qué va a pasar (D-04).
 */
export function Boton({
  children,
  onClick,
  tipo = "button",
  variante = "principal",
  deshabilitado = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: "button" | "submit";
  variante?: "principal" | "secundario" | "terciario" | "peligro";
  deshabilitado?: boolean;
}) {
  return (
    <button
      type={tipo}
      className={`boton boton-${variante}`}
      onClick={onClick}
      disabled={deshabilitado}
      aria-disabled={deshabilitado}
    >
      {children}
    </button>
  );
}

/**
 * Fila de lista que se abre pulsándola ENTERA.
 *
 * Antes la fila sólo era pulsable en su botón "Abrir": un objetivo pequeño al
 * final de una línea de texto que también parecía pulsable. Ahora la fila es un
 * botón de verdad, así que el teclado (tabulador, Intro, Espacio) y los
 * lectores de pantalla funcionan sin nada especial, hay UN solo punto de
 * tabulación por fila, y el nombre que anuncia el lector es el contenido de la
 * fila —número, cliente, estado— en vez de ocho "Abrir" seguidos.
 */
export function FilaPulsable({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <li className="fila">
      <button type="button" className="fila-pulsable" onClick={onClick}>
        <span className="fila-texto">{children}</span>
        <span className="fila-flecha" aria-hidden="true">
          <Icono nombre="siguiente" />
        </span>
      </button>
    </li>
  );
}

/**
 * Campo de texto con etiqueta REAL y error asociado al campo (`D-04`):
 * `aria-invalid` + `aria-describedby`, no un color.
 */
export function Campo({
  id,
  etiqueta,
  valor,
  onChange,
  tipo = "text",
  error = null,
  ayuda = null,
  autoComplete,
  inputMode,
  autoFocus = false,
  requerido = true,
  soloLectura = false,
  mostrar = null,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  tipo?: "text" | "email" | "password" | "date" | "time";
  error?: string | null;
  /** Ayuda permanente (no un error), también asociada al campo. */
  ayuda?: string | null;
  autoComplete?: string;
  inputMode?: "text" | "email" | "numeric";
  autoFocus?: boolean;
  requerido?: boolean;
  /** Visible y seleccionable, pero no editable (p. ej. el NIF tras emitir). */
  soloLectura?: boolean;
  /**
   * Sólo con `tipo="password"`: el botón de ver lo escrito, con sus dos textos
   * (auditoría del onboarding, hallazgo 12). Sin él, una errata en una
   * contraseña de doce caracteres no se ve hasta que el servidor la rechaza.
   */
  mostrar?: { readonly mostrar: string; readonly ocultar: string } | null;
}) {
  const [visible, setVisible] = useState(false);
  const idError = `${id}-error`;
  const idAyuda = `${id}-ayuda`;
  const describedBy = [ayuda !== null ? idAyuda : null, error !== null ? idError : null]
    .filter((x): x is string => x !== null)
    .join(" ");
  const conMostrar = tipo === "password" && mostrar !== null;
  const entrada = (
      <input
        id={id}
        type={conMostrar && visible ? "text" : tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error !== null}
        aria-describedby={describedBy === "" ? undefined : describedBy}
        autoComplete={autoComplete}
        inputMode={inputMode}
        // Sólo el primer campo de la pantalla de entrada lo usa: el usuario
        // viene a escribir ahí. No se pone en formularios largos.
        autoFocus={autoFocus}
        required={requerido}
        readOnly={soloLectura}
      />
  );
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      {conMostrar ? (
        <div className="campo-con-boton">
          {entrada}
          <button
            type="button"
            className="campo-mostrar"
            aria-controls={id}
            aria-pressed={visible}
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? mostrar.ocultar : mostrar.mostrar}
          </button>
        </div>
      ) : (
        entrada
      )}
      {ayuda !== null ? (
        <p className="ayuda-neutra" id={idAyuda}>
          {ayuda}
        </p>
      ) : null}
      {error !== null ? (
        <p className="ayuda-campo" id={idError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Texto de varias líneas. Igual que `Campo` en etiqueta, ayuda y error; sólo
 * cambia el alto. Para notas, donde escribir en una línea de 40 caracteres es
 * incómodo y no se ve lo escrito.
 */
export function AreaTexto({
  id,
  etiqueta,
  valor,
  onChange,
  error = null,
  ayuda = null,
  filas = 5,
  requerido = false,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  error?: string | null;
  ayuda?: string | null;
  filas?: number;
  requerido?: boolean;
}) {
  const idError = `${id}-error`;
  const idAyuda = `${id}-ayuda`;
  const describedBy = [ayuda !== null ? idAyuda : null, error !== null ? idError : null]
    .filter((x): x is string => x !== null)
    .join(" ");
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <textarea
        id={id}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        rows={filas}
        aria-invalid={error !== null}
        aria-describedby={describedBy === "" ? undefined : describedBy}
        required={requerido}
      />
      {ayuda !== null ? (
        <p className="ayuda-neutra" id={idAyuda}>
          {ayuda}
        </p>
      ) : null}
      {error !== null ? (
        <p className="ayuda-campo" id={idError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Desplegable nativo: accesible y usable en móvil sin reinventar nada (`D-01`). */
export function Selector<V extends string>({
  id,
  etiqueta,
  valor,
  opciones,
  onChange,
  error = null,
  deshabilitado = false,
}: {
  id: string;
  etiqueta: string;
  valor: V;
  opciones: ReadonlyArray<{ readonly valor: V; readonly texto: string }>;
  onChange: (v: V) => void;
  error?: string | null;
  deshabilitado?: boolean;
}) {
  const idError = `${id}-error`;
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <select
        id={id}
        value={valor}
        onChange={(e) => onChange(e.target.value as V)}
        aria-invalid={error !== null}
        aria-describedby={error !== null ? idError : undefined}
        disabled={deshabilitado}
      >
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      {error !== null ? (
        <p className="ayuda-campo" id={idError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Buscador con sugerencias: se escribe y el navegador filtra la lista
 * (`input` + `datalist`).
 *
 * `OD-0017 D-01` prohíbe improvisar un combo accesible. Esto no es uno: es el
 * buscador NATIVO del navegador, con su propio teclado, su lectura por
 * lectores de pantalla y su comportamiento en móvil. Cuando la lista crezca
 * más de lo que el navegador maneja bien, el siguiente paso es una primitiva
 * headless accesible, no un desplegable hecho a mano.
 *
 * El texto NO es el valor: `onElegir` recibe el id de la opción cuyo texto
 * coincide exactamente, o `null` mientras no coincide ninguna. Así una
 * búsqueda a medio escribir nunca se confunde con una elección.
 *
 * `claves`: otras formas de escribir la MISMA opción, como el nombre solo o el
 * NIF de un cliente cuya opción es «Nombre · NIF». Se comparan sin tildes ni
 * mayúsculas, y solo eligen si señalan a UNA opción: dos clientes con el mismo
 * nombre siguen obligando a elegir de la lista. Lo pidió el propietario tras
 * probar Presupuestos en staging (2026-10-25): escribió «Carmen Díaz Serrano»
 * entero y el formulario le dijo que el dato era obligatorio.
 */
export function Buscador({
  id,
  etiqueta,
  texto,
  opciones,
  onTexto,
  onElegir,
  ayuda = null,
  error = null,
  marcador,
}: {
  id: string;
  etiqueta: string;
  texto: string;
  opciones: ReadonlyArray<{ readonly id: string; readonly texto: string; readonly claves?: ReadonlyArray<string | null> }>;
  onTexto: (v: string) => void;
  onElegir: (id: string | null) => void;
  ayuda?: string | null;
  error?: string | null;
  /** Texto de ejemplo dentro del campo (no sustituye a la etiqueta). */
  marcador?: string;
}) {
  const idLista = `${id}-lista`;
  const idError = `${id}-error`;
  const idAyuda = `${id}-ayuda`;
  const describedBy = [ayuda !== null ? idAyuda : null, error !== null ? idError : null]
    .filter((x): x is string => x !== null)
    .join(" ");
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="text"
        role="combobox"
        list={idLista}
        value={texto}
        placeholder={marcador}
        onChange={(e) => {
          const v = e.target.value;
          onTexto(v);
          onElegir(opcionQueCoincide(opciones, v));
        }}
        aria-invalid={error !== null}
        aria-describedby={describedBy === "" ? undefined : describedBy}
        autoComplete="off"
      />
      <datalist id={idLista}>
        {opciones.map((o) => (
          <option key={o.id} value={o.texto} />
        ))}
      </datalist>
      {ayuda !== null ? (
        <p className="ayuda-neutra" id={idAyuda}>
          {ayuda}
        </p>
      ) : null}
      {error !== null ? (
        <p className="ayuda-campo" id={idError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Casilla con etiqueta real. */
export function Casilla({
  id,
  etiqueta,
  marcada,
  onChange,
  error = null,
}: {
  id: string;
  etiqueta: string;
  marcada: boolean;
  onChange: (v: boolean) => void;
  error?: string | null;
}) {
  const idError = `${id}-error`;
  return (
    <div className="casilla">
      <input
        id={id}
        type="checkbox"
        checked={marcada}
        onChange={(e) => onChange(e.target.checked)}
        aria-invalid={error !== null}
        aria-describedby={error !== null ? idError : undefined}
      />
      <label htmlFor={id}>{etiqueta}</label>
      {error !== null ? (
        <p className="ayuda-campo" id={idError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Mensaje con `role="alert"`: un lector de pantalla lo anuncia al aparecer. */
export function Aviso({
  tono,
  titulo = null,
  children,
}: {
  tono: "error" | "aviso" | "info" | "exito";
  titulo?: string | null;
  children: ReactNode;
}) {
  return (
    <div className={`aviso aviso-${tono}`} role="alert">
      {titulo !== null ? <strong>{titulo}</strong> : null}
      <p>{children}</p>
    </div>
  );
}

export function Tarjeta({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="tarjeta" aria-label={titulo}>
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}
